import React, { useEffect, useState } from 'react';
import { usePlayerStore } from '../../stores/playerStore';
import { useMatchStore } from '../../stores/matchStore';
import { PlayerStatistics } from '../../types/player';
import { Match } from '../../types/match';
import { 
  TrophyIcon, 
  HandRaisedIcon, 
  XMarkIcon,
  ChartBarIcon,
  UserIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

interface PlayerStatsProps {
  className?: string;
  playerId?: string; // 如果指定，只显示特定玩家的统计
}

const PlayerStats: React.FC<PlayerStatsProps> = ({ 
  className = '',
  playerId 
}) => {
  const { players, statistics, loadStatistics } = usePlayerStore();
  const { getPlayerMatches } = useMatchStore();
  
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(playerId || null);
  const [playerMatches, setPlayerMatches] = useState<Match[]>([]);
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);

  useEffect(() => {
    loadStatistics();
  }, []);

  useEffect(() => {
    if (selectedPlayer) {
      loadPlayerMatches(selectedPlayer);
    }
  }, [selectedPlayer]);

  const loadPlayerMatches = async (playerId: string) => {
    setIsLoadingMatches(true);
    try {
      const matches = await getPlayerMatches(playerId);
      setPlayerMatches(matches);
    } catch (error) {
      console.error('加载玩家比赛记录失败:', error);
    } finally {
      setIsLoadingMatches(false);
    }
  };

  const getPlayerStats = (playerId: string): PlayerStatistics | null => {
    return statistics.find(stat => stat.playerId === playerId) || null;
  };

  const calculateWinRate = (stats: PlayerStatistics): number => {
    if (stats.totalMatches === 0) return 0;
    return Math.round((stats.wins / stats.totalMatches) * 100);
  };

  const getPerformanceLevel = (winRate: number): { label: string; color: string } => {
    if (winRate >= 70) return { label: '优秀', color: 'text-green-600 bg-green-100' };
    if (winRate >= 50) return { label: '良好', color: 'text-blue-600 bg-blue-100' };
    if (winRate >= 30) return { label: '一般', color: 'text-yellow-600 bg-yellow-100' };
    return { label: '需努力', color: 'text-red-600 bg-red-100' };
  };

  const selectedPlayerData = selectedPlayer ? players.find(p => p.id === selectedPlayer) : null;
  const selectedPlayerStats = selectedPlayer ? getPlayerStats(selectedPlayer) : null;

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <h2 className="text-xl font-bold text-gray-800 mb-6">个人统计</h2>

      {/* 玩家选择 */}
      {!playerId && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            选择玩家
          </label>
          <select
            value={selectedPlayer || ''}
            onChange={(e) => setSelectedPlayer(e.target.value || null)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">请选择玩家</option>
            {players.map((player) => (
              <option key={player.id} value={player.id}>
                {player.name} (#{player.number})
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedPlayerData && selectedPlayerStats ? (
        <div className="space-y-6">
          {/* 玩家基本信息 */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center space-x-4">
              <div className="bg-blue-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-xl font-bold">
                {selectedPlayerData.number}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {selectedPlayerData.name}
                </h3>
                <div className="text-sm text-gray-600 flex items-center space-x-4">
                  <span className="flex items-center space-x-1">
                    <UserIcon className="w-4 h-4" />
                    <span>编号: {selectedPlayerData.number}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <CalendarIcon className="w-4 h-4" />
                    <span>加入: {new Date(selectedPlayerData.createdAt).toLocaleDateString('zh-CN')}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 统计概览 */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard
              title="总比赛"
              value={selectedPlayerStats.totalMatches}
              icon={<ChartBarIcon className="w-6 h-6 text-gray-600" />}
              color="bg-gray-50"
            />
            <StatCard
              title="胜利"
              value={selectedPlayerStats.wins}
              icon={<TrophyIcon className="w-6 h-6 text-green-600" />}
              color="bg-green-50"
            />
            <StatCard
              title="平局"
              value={selectedPlayerStats.draws}
              icon={<HandRaisedIcon className="w-6 h-6 text-yellow-600" />}
              color="bg-yellow-50"
            />
            <StatCard
              title="失败"
              value={selectedPlayerStats.losses}
              icon={<XMarkIcon className="w-6 h-6 text-red-600" />}
              color="bg-red-50"
            />
          </div>

          {/* 胜率和表现 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-700 mb-3">胜率分析</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">胜率</span>
                  <span className="text-lg font-bold text-blue-600">
                    {calculateWinRate(selectedPlayerStats)}%
                  </span>
                </div>
                
                {/* 胜率进度条 */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${calculateWinRate(selectedPlayerStats)}%` }}
                  ></div>
                </div>

                {/* 表现等级 */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">表现等级</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    getPerformanceLevel(calculateWinRate(selectedPlayerStats)).color
                  }`}>
                    {getPerformanceLevel(calculateWinRate(selectedPlayerStats)).label}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-700 mb-3">比赛分布</h4>
              <div className="space-y-2">
                {selectedPlayerStats.totalMatches > 0 ? (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-green-600">胜利</span>
                      <span>{Math.round((selectedPlayerStats.wins / selectedPlayerStats.totalMatches) * 100)}%</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-yellow-600">平局</span>
                      <span>{Math.round((selectedPlayerStats.draws / selectedPlayerStats.totalMatches) * 100)}%</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-red-600">失败</span>
                      <span>{Math.round((selectedPlayerStats.losses / selectedPlayerStats.totalMatches) * 100)}%</span>
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-gray-500 text-center py-4">
                    暂无比赛数据
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 最近比赛记录 */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-4">最近比赛记录</h4>
            {isLoadingMatches ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse bg-gray-200 h-16 rounded-lg"></div>
                ))}
              </div>
            ) : playerMatches.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {playerMatches.slice(0, 10).map((match) => (
                  <RecentMatchCard 
                    key={match.id} 
                    match={match} 
                    playerId={selectedPlayer!}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <ChartBarIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>暂无比赛记录</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-500 py-12">
          <UserIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg mb-2">请选择玩家</p>
          <p className="text-sm">选择一个玩家查看详细统计信息</p>
        </div>
      )}
    </div>
  );
};

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => {
  return (
    <div className={`${color} rounded-lg p-4 text-center`}>
      <div className="flex justify-center mb-2">
        {icon}
      </div>
      <div className="text-2xl font-bold text-gray-800 mb-1">
        {value}
      </div>
      <div className="text-sm text-gray-600">
        {title}
      </div>
    </div>
  );
};

interface RecentMatchCardProps {
  match: Match;
  playerId: string;
}

const RecentMatchCard: React.FC<RecentMatchCardProps> = ({ match, playerId }) => {
  // 判断玩家在哪个队伍
  const isInTeam1 = match.pairing.team1.some(p => p.id === playerId);
  const playerTeam = isInTeam1 ? match.pairing.team1 : match.pairing.team2;
  const opponentTeam = isInTeam1 ? match.pairing.team2 : match.pairing.team1;
  
  // 判断玩家的比赛结果
  let playerResult: 'win' | 'draw' | 'loss';
  if (match.result === 'draw') {
    playerResult = 'draw';
  } else if (
    (isInTeam1 && match.result === 'win') || 
    (!isInTeam1 && match.result === 'loss')
  ) {
    playerResult = 'win';
  } else {
    playerResult = 'loss';
  }

  const getResultColor = (result: string) => {
    switch (result) {
      case 'win':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'draw':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'loss':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getResultIcon = (result: string) => {
    switch (result) {
      case 'win':
        return <TrophyIcon className="w-4 h-4 text-green-600" />;
      case 'draw':
        return <HandRaisedIcon className="w-4 h-4 text-yellow-600" />;
      case 'loss':
        return <XMarkIcon className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getResultText = (result: string) => {
    switch (result) {
      case 'win':
        return '胜利';
      case 'draw':
        return '平局';
      case 'loss':
        return '失败';
      default:
        return '未知';
    }
  };

  return (
    <div className={`border-2 rounded-lg p-3 ${getResultColor(playerResult)}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {getResultIcon(playerResult)}
          <div>
            <div className="font-medium text-sm">
              {getResultText(playerResult)}
            </div>
            <div className="text-xs opacity-75">
              {new Date(match.playedAt).toLocaleDateString('zh-CN')}
            </div>
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-sm font-medium">
            搭档: {playerTeam.find(p => p.id !== playerId)?.name}
          </div>
          <div className="text-xs opacity-75">
            对手: {opponentTeam.map(p => p.name).join(' & ')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerStats;